import { BllErrorCode } from "@arbeidsark/shared"
import type React from "react"
import { useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { Button, Form, FormField, Input, Label } from "~/components"
import { FullPageLoader } from "~/components/Loader"
import { captureEvent, identifyUser } from "~/modules/analytics/capture"
import { Margin } from "~/ur-react-components"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useKeyDown, useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { trpc } from "~/utils/trpc"
import { validateEmail } from "~/utils/validation"
import { setAuthToken } from "./util"

const Title = styled.h3`
  font-family: 'Work Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const BottomSection = styled.div`
  text-align: center;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`

const RegisterUserText = styled.p`
  color: ${(props) => props.theme.colors.gray0};
  margin: 0.5rem;
`

const RegisterUserLink = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.primary};
  font-weight: bold;
`

const RegisterSingleUser: React.FC = () => {
  const translations = useTranslate({
    tryFree: "subscriptions.try-for-free-30-days",
    register: "common.register",

    firstName: "common.first-name",
    lastName: "common.last-name",
    email: "common.email",
    password: "common.password",
    passwordAgain: "login.repeat-password",

    singleUser: "common.single-user",
    school: "common.school",

    alreadyRegistered: "register.already-registered",
    login: "common.log-in",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "errors.from-code.email-already-taken",
      wrongUsernameOrPassword: "error.wrong-username-or-password",
      required: ["error.field-is-required", { field: "" }],
      invalidEmail: "errors.invalid-email-address",
      badPasswords: "errors.change-password-weak",
      nonMatchingPasswords: "errors.passwords-do-not-match",
    },
  })

  const {
    formValues: form,
    formErrors,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm({
    values: {
      email: "",
    },
    validators: {
      email: validateEmail(translations.error.invalidEmail),
    },
  })

  useEffect(() => {
    captureEvent("start_signup_process")
  }, [])

  const handleEnter = useKeyDown("Enter", submit(handleSubmit))

  const navigate = useNavigate()

  const { data: isLoggedIn, isLoading: isLoggedInLoading } =
    trpc.authentication.isLoggedIn.useQuery()

  const toast = useToast()
  const { getToastInfo } = useErrorHandling()

  const { mutate: register, isPending: loginLoading } =
    trpc.authentication.signup.useMutation({
      onSuccess(data) {
        setAuthToken(data.token)

        identifyUser(data.user.id.toString(), {
          email: data.user.email,
          name: (data.user.firstName + " " + data.user.lastName).trim(),
          is_system_admin: data.user.role === "ADMIN",
        })
        captureEvent("signup")
        navigate("/")
      },
      onError(error) {
        // Parse the error message to extract the error type
        const errorTypeMatch = error.message.match(/\[Type\]: ([^\n]+)/)
        const errorType = errorTypeMatch ? errorTypeMatch[1].trim() : null
        
        if (errorType === BllErrorCode.EmailAlreadyTaken) {
          toast("error", translations.error.emailTaken)
          return
        }

        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )

        toast(type, message)
      },
    })

  const submitDisabled = useMemo(
    () => loginLoading || !form.email,
    [loginLoading, form.email],
  )

  async function handleSubmit() {
    if (submitDisabled) return

    register({
      email: form.email,
    })
  }

  useEffect(() => {
    if (isLoggedIn) navigate("/")
  }, [isLoggedIn, navigate])

  if (isLoggedInLoading) return <FullPageLoader />

  return (
    <>
      <Title>{translations.tryFree}</Title>

      <Form preventDefault onSubmit={submit(handleSubmit)}>
        <FormField margin="1rem">
          <Label>{translations.email}</Label>
          <Input
            value={form.email}
            error={formErrors.email}
            width="100%"
            disabled={isLoggedInLoading}
            onKeyDown={handleEnter}
            onChange={handler("email")}
          />
        </FormField>
        <Margin top="1.5rem">
          <Button
            width="100%"
            disabled={submitDisabled}
            onClick={submit(handleSubmit)}
          >
            {translations.register}
          </Button>
        </Margin>
      </Form>
      <BottomSection>
        <RegisterUserText>{translations.alreadyRegistered}</RegisterUserText>
        <RegisterUserLink to="/login">{translations.login}</RegisterUserLink>
      </BottomSection>
    </>
  )
}

export default RegisterSingleUser
